'use client'

import { type ReactNode, createContext, useContext, useMemo, useState } from 'react'

import type { ProfileName } from '@/lib/api/user-preferences/user-preferences.types'

type UserProfileContextType = {
  email: string
  /** Stored name. Null when the user has never saved one. */
  fullName: ProfileName | null
  /** `First Last` from the stored name. Null when there is no name to show. */
  displayName: string | null
  /** Updates the shown name only; persisting is the caller's job (`persistFullName`). */
  setFullName: (fullName: ProfileName) => void
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined)

type UserProfileProviderProps = {
  children: ReactNode
  email: string
  /** The stored name, read on the server. */
  fullName: ProfileName | null
}

const UserProfileProvider = (props: UserProfileProviderProps) => {
  const { children, email, fullName: initialFullName } = props
  const [fullName, setFullName] = useState(initialFullName)

  const value = useMemo(() => {
    const displayName = fullName
      ? `${fullName.firstName.trim()} ${fullName.lastName.trim()}`.trim()
      : ''

    return { email, fullName, displayName: displayName || null, setFullName }
  }, [email, fullName])

  return (
    <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
  )
}

const useUserProfile = () => {
  const context = useContext(UserProfileContext)
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider')
  }
  return context
}

export { UserProfileProvider, useUserProfile }
